import React, { Component } from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import { Dialog } from 'material-ui'
import { Tabs, Tab } from 'material-ui/Tabs'
import CallLogPage from './CallLogPage'
import ContactsTab from './ContactsTab'
import ContactPopover from './ContactPopover'
import { renderAlert } from '../../services/render-alert'
import {
  clearCallData,
  fetchContacts,
  fetchCoredialCalls,
  saveContact,
  searchContacts,
  setCallLogFilter,
  setDate,
  setMatchingContacts,
  setNewContactInfo,
  setPopoverPage,
  setSelectedTab,
} from '../../state/actions/call-log'
import {
  selectMenu,
  updatePatientSearch,
  updatePatientsAllLocations,
} from '../../state/actions/patient-groups'
import { getMockCallData } from '../../data/mockCallData'

require('react-datepicker/dist/react-datepicker.css')

const tabStyles = {
  tab: {
    backgroundColor: 'white',
    color: '#555555',
  },
  activeTab: {
    backgroundColor: '#ffeb3b',
    color: '#555555',
  },
  underline: {
    height: '4px',
    marginTop: '-4px',
    backgroundColor: 'unset',
  },
}

class CallLog extends Component {
  componentDidMount() {
    this.getCallRecords(this.props.date)
  }

  componentWillReceiveProps({ activeLocation: nextActiveLocation }) {
    const {
      fetchCallDataFromBackend,
      timeZone,
      activeLocation,
      date,
    } = this.props
    if (activeLocation && nextActiveLocation && activeLocation.id !== nextActiveLocation.id) {
      fetchCallDataFromBackend(date, timeZone, true, nextActiveLocation.id)
    }
  }

  getCallRecords = (selectedDate) => {
    const {
      fetchFromState,
      fetchCallDataFromBackend,
      callDataByDate,
      timeZone,
      activeLocation,
    } = this.props
    // If the data is already loaded into state, load page first then fetch data after
    if (callDataByDate[selectedDate.format('YYYY-MM-DD')]) {
      fetchFromState(selectedDate)
    } else {
      const locationId = activeLocation && activeLocation.id
      fetchCallDataFromBackend(selectedDate, timeZone, true, locationId)
    }
  }

  createStyles = () => {
    const { currentSelectedTab } = this.props
    return ['callLog', 'contacts'].reduce((acc, tab) => {
      acc[`${tab}`] =
        tab === currentSelectedTab ? tabStyles.activeTab : tabStyles.tab
      return acc
    }, {})
  }

  searchContacts = async () => {
    const {
      newContactInfo: { firstName, lastName, email },
      saveMatches,
      togglePopover,
      searchContactsFromBackend,
    } = this.props
    try {
      const result = await searchContactsFromBackend(
        firstName,
        lastName,
        email,
      )
      saveMatches(result.matching_contacts)
      togglePopover('updateContact')
    } catch (err) {
      renderAlert(err.message, 'error')
    }
  }

  saveContact = (type = null, contactableId = null) => {
    const {
      newContactInfo: {
        firstName,
        lastName,
        email,
        phoneNumber,
        attribution,
        otherAttribution,
        id,
      },
      clearCallDataInState,
      fetchCallDataFromBackend,
      saveContactToBackend,
      date,
      timeZone,
      activeLocation,
    } = this.props

    const locationId = activeLocation && activeLocation.id
    const contactInfo = {
      first_name: firstName,
      last_name: lastName,
      email,
      phone_number: phoneNumber,
      type,
      contactable_id: contactableId,
      contact_id: id,
      attribution,
      other_attribution: otherAttribution,
    }

    saveContactToBackend(contactInfo).then(() => {
      clearCallDataInState()
      fetchCallDataFromBackend(date, timeZone, true, locationId)
    })
  }

  createDateArr = (selectedDate) => {
    const dateArr = [-2, -1, 0, 1, 2]
    return dateArr.map(day =>
      selectedDate.clone().add(day, 'day').format('YYYY-MM-DD'),
    )
  }

  handleDateSelectorClick = (val) => {
    this.getCallRecords(val)
  }

  handleLeftArrowClick = () => {
    this.getCallRecords(this.props.date.clone().add(-1, 'day'))
  }

  handleRightArrowClick = (disabledRight) => {
    if (!disabledRight) {
      this.getCallRecords(this.props.date.clone().add(1, 'day'))
    }
  }

  handleCallLogClick = (phoneNumber, known) => {
    const {
      setFilter,
      searchPatients,
      history,
      togglePopover,
      changeNewContactInfo,
      updatePatientsAllLocations,
    } = this.props
    if (known) {
      updatePatientsAllLocations(true)
      setFilter('all', 'filters', 'list')
      searchPatients(phoneNumber)
      history.push('/patients')
    } else {
      togglePopover('enterContactInfo')
    }
    changeNewContactInfo('phoneNumber', phoneNumber)
  }

  handleContactEdit = (
    fname,
    lname,
    email,
    phone,
    id,
    attribution,
    otherAttribution,
  ) => {
    const { togglePopover, changeNewContactInfo } = this.props
    changeNewContactInfo('firstName', fname)
    changeNewContactInfo('lastName', lname)
    changeNewContactInfo('email', email)
    changeNewContactInfo('phoneNumber', phone)
    changeNewContactInfo('attribution', attribution)
    changeNewContactInfo('otherAttribution', otherAttribution)
    changeNewContactInfo('id', id)
    togglePopover('enterContactInfo')
  }

  resetToToday = () => {
    this.getCallRecords(moment())
  }

  handleDateChange = (newDate) => {
    this.getCallRecords(newDate)
  }

  closePopover = () => {
    const { togglePopover, changeNewContactInfo } = this.props
    togglePopover('')
    changeNewContactInfo('firstName', '')
    changeNewContactInfo('lastName', '')
    changeNewContactInfo('email', '')
    changeNewContactInfo('phoneNumber', '')
    changeNewContactInfo('attribution', '')
    changeNewContactInfo('otherAttribution', '')
    changeNewContactInfo('id', null)
  }

  backPopover = () => {
    const { togglePopover } = this.props
    togglePopover('enterContactInfo')
  }

  handleExistingContactClick = async (contact) => {
    const { getContacts } = this.props
    const contactType = ('doctor_id' in contact) ? 'Patient' : 'ResponsibleParty'
    await this.saveContact(contactType, contact.id)
    getContacts()
    this.closePopover()
  }

  handleNewContactClick = async () => {
    const { getContacts } = this.props
    await this.saveContact()
    getContacts()
    this.closePopover()
  }

  render() {
    const {
      date,
      callDataByDate,
      callLogFilter,
      selectCallLogFilter,
      isFetching,
      popoverPage,
      newContactInfo,
      changeNewContactInfo,
      matchingContacts,
      currentSelectedTab,
      toggleTabs,
      contacts,
      contactsFetched,
      conversionStats,
      getContacts,
      practiceName,
    } = this.props
    const dateFormatted = date.format('YYYY-MM-DD')
    const dateArr = this.createDateArr(date)
    const styles = this.createStyles()

    let callData
    if (practiceName === 'Feathered Smiles Orthodontics') {
      callData = getMockCallData()
    } else {
      callData = callDataByDate
    }
    
    return (
      <div>
        <Tabs
          value={currentSelectedTab}
          onChange={toggleTabs}
          inkBarStyle={tabStyles.underline}
        >
          <Tab label="Call Log" value="callLog" style={styles.callLog}>
            <CallLogPage
              callDataByDate={callData}
              selectCallLogFilter={selectCallLogFilter}
              date={date}
              dateArr={dateArr}
              dateFormatted={dateFormatted}
              handleCallLogClick={this.handleCallLogClick}
              handleDateChange={this.handleDateChange}
              handleDateSelectorClick={this.handleDateSelectorClick}
              handleLeftArrowClick={this.handleLeftArrowClick}
              handleRightArrowClick={this.handleRightArrowClick}
              isFetching={isFetching}
              resetToToday={this.resetToToday}
              callLogFilter={callLogFilter}
            />
          </Tab>
          <Tab label="Contacts" value="contacts" style={styles.contacts}>
            <ContactsTab
              contacts={contacts}
              conversionStats={conversionStats}
              contactsFetched={contactsFetched}
              getContacts={getContacts}
              handleContactEdit={this.handleContactEdit}
            />
          </Tab>
        </Tabs>
        <Dialog
          open={popoverPage !== ''}
          onRequestClose={this.closePopover}
          contentStyle={{ width: '600px' }}
          autoScrollBodyContent
          zDepth={2}
        >
          <ContactPopover
            popoverPage={popoverPage}
            newContactInfo={newContactInfo}
            onDiscardClick={this.closePopover}
            onSaveClick={this.searchContacts}
            onBackClick={this.backPopover}
            onChangeContactInfo={(contactInfoType, e) =>
              changeNewContactInfo(contactInfoType, e.target.value)
            }
            onDropdownSelect={(contactInfoType, value) =>
              changeNewContactInfo(contactInfoType, value)
            }
            matchingContacts={matchingContacts}
            handleExistingContactClick={contact =>
              this.handleExistingContactClick(contact)
            }
            handleNewContactClick={() => this.handleNewContactClick()}
          />
        </Dialog>
      </div>
    )
  }
}

CallLog.propTypes = {
  activeLocation: PropTypes.shape({
    active: PropTypes.bool.isRequired,
    additional_recipients: PropTypes.arrayOf(PropTypes.object),
    city: PropTypes.string,
    country_code: PropTypes.string,
    created_at: PropTypes.string,
    doctor_id: PropTypes.string,
    earliest_same_day_reminder_hour: PropTypes.number,
    facebook_url: PropTypes.string,
    forwarding_email: PropTypes.string,
    google_url: PropTypes.string,
    id: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    name: PropTypes.string,
    office_email: PropTypes.string,
    office_phone: PropTypes.string,
    phone_numbers: PropTypes.arrayOf(PropTypes.object),
    practice_id: PropTypes.number,
    state: PropTypes.string,
    street_address: PropTypes.string,
    timezone: PropTypes.string,
    updated_at: PropTypes.string,
    website: PropTypes.string,
    yelp_url: PropTypes.string,
    zip: PropTypes.string,
  }).isRequired,
  callDataByDate: PropTypes.object.isRequired,
  changeNewContactInfo: PropTypes.func.isRequired,
  clearCallDataInState: PropTypes.func.isRequired,
  selectCallLogFilter: PropTypes.func.isRequired,
  contacts: PropTypes.array,
  contactsFetched: PropTypes.bool,
  conversionStats: PropTypes.object.isRequired,
  currentSelectedTab: PropTypes.string,
  date: PropTypes.object.isRequired,
  fetchCallDataFromBackend: PropTypes.func.isRequired,
  saveContactToBackend: PropTypes.func.isRequired,
  getContacts: PropTypes.func.isRequired,
  fetchFromState: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  isFetching: PropTypes.bool.isRequired,
  matchingContacts: PropTypes.array.isRequired,
  newContactInfo: PropTypes.object.isRequired,
  popoverPage: PropTypes.string.isRequired,
  practiceName: PropTypes.string.isRequired,
  saveMatches: PropTypes.func.isRequired,
  searchPatients: PropTypes.func.isRequired,
  updatePatientsAllLocations: PropTypes.func.isRequired,
  setFilter: PropTypes.func.isRequired,
  timeZone: PropTypes.string.isRequired,
  togglePopover: PropTypes.func.isRequired,
  toggleTabs: PropTypes.func.isRequired,
  callLogFilter: PropTypes.string.isRequired,
}

const mapStateToProps = ({
  callLog: {
    callDataByDate,
    contacts,
    contactsFetched,
    conversionStats,
    currentSelectedTab,
    date,
    isFetching,
    matchingContacts,
    newContactInfo,
    popoverPage,
    timeZone,
    filter,
  },
  account: {
    user: { activeLocation, practiceName },
  },
}) => ({
  callDataByDate,
  contacts,
  contactsFetched,
  conversionStats,
  currentSelectedTab,
  date,
  isFetching,
  matchingContacts,
  newContactInfo,
  popoverPage,
  timeZone,
  callLogFilter: filter,
  activeLocation,
  practiceName,
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      fetchFromState: date => setDate(date),
      fetchCallDataFromBackend: (
        selectedDate,
        timeZone,
        changeDate,
        locationId,
      ) => fetchCoredialCalls(selectedDate, timeZone, changeDate, locationId),
      saveContactToBackend: body => saveContact(body),
      searchContactsFromBackend: (firstName, lastName, email) =>
        searchContacts(firstName, lastName, email),
      getContacts: fetchContacts,
      clearCallDataInState: clearCallData,
      setFilter: (menuId, menuType, view) => selectMenu(menuId, menuType, view),
      searchPatients: number => updatePatientSearch(number),
      updatePatientsAllLocations: checked => updatePatientsAllLocations(checked),
      selectCallLogFilter: filter => setCallLogFilter(filter),
      togglePopover: pageName => setPopoverPage(pageName),
      toggleTabs: heading => setSelectedTab(heading),
      changeNewContactInfo: (contactInfoType, value) =>
        setNewContactInfo(contactInfoType, value),
      saveMatches: matchingContacts => setMatchingContacts(matchingContacts),
    },
    dispatch,
  )

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps,
)(CallLog))



// WEBPACK FOOTER //
// ./src/components/CallLog/index.js